// The search order form: an address in, the certificates a section 32 needs
// out, ticked per title, saved as an order staff place with the supplier.
//
// **Everything about what to order is the middleware's.** Which certificates a
// property needs, how many owners corporations it has, which water authority,
// what each costs through each channel — all of it arrives on
// `POST /search-order/options` and is rendered here, never worked out. The
// total shown while ticking is a convenience; the order is priced again on the
// server when it is saved, and the saved total is the one that counts. See the
// middleware's docs/decisions/search-orders.md.
//
// **A column per title.** A unit and its car park, a storage cage, two adjoining
// blocks: each is a column, and a certificate row is ticked per column. The
// first column is the main property; an added one says what it is, and the
// middleware decides its defaults from that.
//
// **Ordered from a matter, it is for that matter.** The matter's address is
// filled in and its reference fixed, and the middleware writes each line onto
// the matter as a search waiting for its document — the nightly import never
// sees an order placed from here. An order saved without a matter can be
// attached to one later from the Orders list.
//
// `api` is App.js's `apiFetch`, handed in rather than imported, so the access
// key is attached in exactly one place.

import { useEffect, useMemo, useState } from "react";
import { T } from "./shared";

const MAX_COLUMNS = 6;
const CHANNEL_LABEL = {
  trisearch: "Trisearch",
  infotrack_manual: "InfoTrack (manual order)",
  landata: "Landata direct",
};
const KIND_OPTIONS = [
  ["car_park", "Car park"],
  ["storage", "Storage"],
  ["other", "Other title"],
];
const LINE_STATUS = [
  ["requested", "To order"],
  ["placed", "Placed"],
  ["not_required", "Not required"],
  ["cancelled", "Cancelled"],
];

const money = (n) => (n == null ? "price on order" : `$${Number(n).toFixed(2)}`);

// ── The pieces worth testing ────────────────────────────────────────────────

// Rows of the grid: one per certificate, in the order the middleware sent
// them, each holding every column's lines for it — a column with two titles
// has two lines in the title row.
export const rowsOf = (columns = []) => {
  const rows = [];
  const byProduct = new Map();
  columns.forEach((col) => {
    col.lines.forEach((line) => {
      let row = byProduct.get(line.productId);
      if (!row) {
        row = { productId: line.productId, name: line.name, group: line.group, cells: {} };
        byProduct.set(line.productId, row);
        rows.push(row);
      }
      (row.cells[col.column] = row.cells[col.column] || []).push(line);
    });
  });
  // Title certificates first, as Landata lays them out.
  return [...rows.filter(r => r.group === "title"), ...rows.filter(r => r.group !== "title")];
};

// The ticks after a refresh: what somebody changed by hand survives for every
// line still on offer; a line new to this answer takes the middleware's default.
export const mergeSelection = (previous = {}, columns = []) => {
  const next = {};
  columns.forEach(col => col.lines.forEach((line) => {
    next[line.key] = Object.prototype.hasOwnProperty.call(previous, line.key) ? previous[line.key] : Boolean(line.selected);
  }));
  return next;
};

// What a save sends: line keys, with the water authority where somebody had to
// choose one. Never a price.
export const selectionPayload = (columns = [], selected = {}, waterChoice = {}) => {
  const out = [];
  columns.forEach(col => col.lines.forEach((line) => {
    if (!selected[line.key]) return;
    if (line.needsChoice) {
      if (waterChoice[line.key]) out.push({ key: line.key, authority: waterChoice[line.key] });
      return;
    }
    out.push(line.key);
  }));
  return out;
};

// The running total while ticking — a guide; the saved order is priced on the
// server. Lines with no price, and placeholders, are counted apart.
export const runningTotal = (columns = [], selected = {}) => {
  let total = 0;
  let unpriced = 0;
  columns.forEach(col => col.lines.forEach((line) => {
    if (!selected[line.key] || line.deferred) return;
    if (line.price == null || line.needsChoice) unpriced += 1;
    else total += line.price;
  }));
  return { total: Math.round(total * 100) / 100, unpriced };
};

// A water line that needs a choice cannot be saved without one.
export const missingChoices = (columns = [], selected = {}, waterChoice = {}) =>
  columns.flatMap(col => col.lines.filter(l => l.needsChoice && selected[l.key] && !waterChoice[l.key]));

// ── Small pieces of UI ──────────────────────────────────────────────────────

const inputStyle = {
  fontFamily: "Inter, sans-serif", fontSize: 13, padding: "8px 10px", borderRadius: 8,
  border: `1px solid ${T.borderMid}`, background: T.white, color: T.text, boxSizing: "border-box",
};

const Button = ({ children, onClick, primary = false, disabled = false, type = "button", title, style = {} }) => (
  <button type={type} onClick={onClick} disabled={disabled} title={title}
    style={{
      fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 500, padding: "8px 16px", borderRadius: 999,
      border: `1px solid ${primary ? T.text : T.borderMid}`, background: primary ? T.text : T.white,
      color: primary ? T.white : T.text, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
      ...style,
    }}>
    {children}
  </button>
);

const Note = ({ children, tone = "soft" }) => (
  <div style={{ fontSize: 11.5, lineHeight: 1.45, color: tone === "warn" ? T.red : T.textFaint, marginTop: 3 }}>{children}</div>
);

// ── The new order ───────────────────────────────────────────────────────────

const emptyColumn = (kind, address = "") => ({ address, pfi: "", kind, volFolio: {} });

const NewOrder = ({ api, onSaved, forMatter = null }) => {
  const [properties, setProperties] = useState([emptyColumn("main", (forMatter && forMatter.address) || "")]);
  const [channel, setChannel] = useState("trisearch");
  const [grid, setGrid] = useState(null);
  const [choice, setChoice] = useState(null); // { column, candidates }
  const [selected, setSelected] = useState({});
  const [waterChoice, setWaterChoice] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [matterRef, setMatterRef] = useState("");
  const [notes, setNotes] = useState("");

  // Ask for the grid. Only the columns that have an address are sent; a
  // column waiting on its address simply is not in the grid yet.
  const refresh = async (props = properties, ch = channel) => {
    // The server numbers only the columns it was sent, so its column N is the
    // Nth property here that has an address — kept, so a vol/fol or a pick
    // lands on the right row.
    const index = props.map((p, i) => (p.address.trim() ? i : -1)).filter(i => i >= 0);
    const asked = index.map(i => props[i]);
    if (!asked.length) return;
    setBusy(true); setError("");
    try {
      const r = await api("/search-order/options", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ properties: asked, channel: ch }),
      });
      const data = await r.json().catch(() => ({}));
      if (data.needsChoice) {
        setChoice({ column: data.column, at: index[data.column - 1], candidates: data.candidates || [], error: data.error || "" });
        return;
      }
      if (!r.ok || !data.ok) throw new Error(data.error || `The order form answered ${r.status}`);
      setChoice(null);
      setGrid({ ...data, index });
      setSelected(prev => mergeSelection(prev, data.columns));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Ordered from a matter: its address is already here, so look it up.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (forMatter && forMatter.address) refresh(); }, []);

  const setColumn = (i, patch) => setProperties(ps => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  const pick = (pfi) => {
    const i = choice.at;
    const next = properties.map((p, j) => (j === i ? { ...p, pfi } : p));
    setProperties(next);
    refresh(next);
  };

  const addColumn = () => setProperties(ps => [...ps, emptyColumn("car_park")]);
  const removeColumn = (i) => {
    const next = properties.filter((_, j) => j !== i);
    setProperties(next);
    if (next.some(p => p.address.trim())) refresh(next); else setGrid(null);
  };

  const columns = useMemo(() => (grid ? grid.columns : []), [grid]);
  const rows = useMemo(() => rowsOf(columns), [columns]);
  const { total, unpriced } = runningTotal(columns, selected);
  const missing = missingChoices(columns, selected, waterChoice);
  const payload = selectionPayload(columns, selected, waterChoice);

  const save = async () => {
    setBusy(true); setError("");
    try {
      const r = await api("/search-orders", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          properties: properties.filter(p => p.address.trim()), channel, lines: payload,
          matterRef: (forMatter ? forMatter.ref : matterRef.trim()) || undefined, notes,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || `Saving the order answered ${r.status}`);
      onSaved(data.order, data.matter);
      setProperties([emptyColumn("main")]); setGrid(null); setSelected({}); setWaterChoice({});
      setMatterRef(""); setNotes("");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {/* The properties: the main one, and any car park, storage or other
          title added beside it. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
        {properties.map((p, i) => (
          <form key={i} onSubmit={(e) => { e.preventDefault(); setColumn(i, { pfi: "" }); refresh(properties.map((q, j) => (j === i ? { ...q, pfi: "" } : q))); }}
            style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: T.textFaint, width: 76 }}>{i === 0 ? "Property" : `Title ${i + 1}`}</span>
            {i > 0 && (
              <select aria-label={`What title ${i + 1} is`} value={p.kind} onChange={(e) => setColumn(i, { kind: e.target.value })} style={inputStyle}>
                {KIND_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            )}
            <input aria-label={i === 0 ? "Property address" : `Address of title ${i + 1}`} value={p.address}
              onChange={(e) => setColumn(i, { address: e.target.value, pfi: "" })}
              placeholder={i === 0 ? "Unit 2, 4 Kurrajong Avenue, Glen Waverley 3150" : "Its address — a car park often has its own unit number"}
              style={{ ...inputStyle, flex: "1 1 320px" }} />
            <Button type="submit" primary={i === 0} disabled={busy || !p.address.trim()}>Find</Button>
            {i > 0 && <Button onClick={() => removeColumn(i)}>Remove</Button>}
          </form>
        ))}
        {properties.length < MAX_COLUMNS && grid && (
          <div><Button onClick={addColumn}>+ Add another title (car park, storage…)</Button></div>
        )}
      </div>

      {choice && (
        <div role="group" aria-label="Which property" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 8 }}>
            {choice.candidates.length ? `Which property is ${choice.at === 0 ? "it" : `title ${choice.at + 1}`}?` : (choice.error || "Nothing found at that address.")}
          </div>
          {choice.candidates.map(c => (
            <button key={c.propertyPfi} type="button" onClick={() => pick(c.propertyPfi)}
              style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", marginBottom: 6, cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: 13, color: T.text }}>
              {c.address}{c.parcelCount > 1 ? <span style={{ color: T.textFaint }}> · {c.parcelCount} titles</span> : null}
            </button>
          ))}
        </div>
      )}

      {error && <div role="alert" style={{ color: T.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {grid && (
        <>
          {grid.warnings && grid.warnings.length > 0 && (
            <div style={{ background: T.white, border: `1px solid ${T.borderMid}`, borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
              {grid.warnings.map(w => <div key={w} style={{ fontSize: 12.5, color: T.text }}>{w}</div>)}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12.5, color: T.textSoft }}>
              Ordering through{" "}
              <select aria-label="Ordering through" value={channel}
                onChange={(e) => { setChannel(e.target.value); refresh(properties, e.target.value); }} style={inputStyle}>
                {(grid.channels || Object.keys(CHANNEL_LABEL)).map(c => <option key={c} value={c}>{CHANNEL_LABEL[c] || c}</option>)}
              </select>
            </label>
            {busy && <span style={{ fontSize: 12, color: T.textFaint }}>Updating…</span>}
          </div>

          <div style={{ overflowX: "auto", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontFamily: "Inter, sans-serif", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: `1px solid ${T.border}`, color: T.textSoft, fontWeight: 500, minWidth: 220 }}>Certificate</th>
                  {columns.map(col => (
                    <th key={col.column} style={{ textAlign: "left", padding: "10px 12px", borderBottom: `1px solid ${T.border}`, borderLeft: `1px solid ${T.border}`, minWidth: 220, verticalAlign: "top" }}>
                      <div style={{ fontSize: 11, color: T.textFaint, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>{col.label}</div>
                      <div style={{ color: T.text, fontWeight: 500 }}>{col.property.address}</div>
                      {col.property.council && <div style={{ fontSize: 11.5, color: T.textFaint, fontWeight: 400 }}>{col.property.council}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.productId}>
                    <td style={{ padding: "8px 12px", verticalAlign: "top", borderBottom: `1px solid ${T.border}` }}>{row.name}</td>
                    {columns.map(col => (
                      <td key={col.column} style={{ padding: "8px 12px", verticalAlign: "top", borderBottom: `1px solid ${T.border}`, borderLeft: `1px solid ${T.border}` }}>
                        {(row.cells[col.column] || []).map(line => (
                          <LineCell key={line.key} line={line}
                            checked={Boolean(selected[line.key])}
                            onToggle={() => setSelected(s => ({ ...s, [line.key]: !s[line.key] }))}
                            water={waterChoice[line.key] || ""}
                            onWater={(a) => setWaterChoice(w => ({ ...w, [line.key]: a }))}
                            authorities={grid.waterAuthorities || []}
                            volFolio={((properties[grid.index[col.column - 1]] || {}).volFolio || {})[line.target && line.target.spi] || ""}
                            onVolFolio={(vf) => {
                              const i = grid.index[col.column - 1];
                              if (i === undefined) return;
                              const next = properties.map((p, j) => (j === i ? { ...p, volFolio: { ...p.volFolio, [line.target.spi]: vf } } : p));
                              setProperties(next);
                              refresh(next);
                            }}
                          />
                        ))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 14, flexWrap: "wrap" }}>
            {forMatter
              ? <div style={{ fontSize: 13, color: T.textSoft, padding: "8px 0", width: 220 }}>For matter <span style={{ color: T.text, fontWeight: 500 }}>{forMatter.ref}</span></div>
              : <input aria-label="Matter reference" value={matterRef} onChange={(e) => setMatterRef(e.target.value)}
                  placeholder="Matter reference (optional)" style={{ ...inputStyle, width: 220 }} />}
            <textarea aria-label="Notes for whoever places it" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes for whoever places it" rows={2} style={{ ...inputStyle, flex: "1 1 280px", resize: "vertical" }} />
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div style={{ fontSize: 18, fontWeight: 500, color: T.text }}>${total.toFixed(2)}</div>
              <div style={{ fontSize: 11.5, color: T.textFaint }}>
                {payload.length} certificate{payload.length === 1 ? "" : "s"}
                {unpriced ? ` · ${unpriced} priced on order` : ""} · checked again when saved
              </div>
              {missing.length > 0 && <Note tone="warn">Choose the water authority for {missing.length === 1 ? "the ticked water statement" : `${missing.length} water statements`}.</Note>}
              <Button primary style={{ marginTop: 8 }} disabled={busy || !payload.length || missing.length > 0} onClick={save}>Save order</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const LineCell = ({ line, checked, onToggle, water, onWater, authorities, volFolio, onVolFolio }) => {
  const [vf, setVf] = useState(volFolio);
  useEffect(() => setVf(volFolio), [volFolio]);
  const label = line.target && line.target.label;
  const isTitle = line.productId === "title";
  const choices = line.needsChoice
    ? [...new Set([...(line.target.choices || []), ...authorities])]
    : [];
  return (
    <div style={{ marginBottom: 6 }}>
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
        <input type="checkbox" checked={checked} onChange={onToggle} aria-label={`${line.name}${label ? ` — ${label}` : ""}`} style={{ marginTop: 2 }} />
        <span>
          {[
            label && <span key="l" style={{ color: T.text }}>{label}</span>,
            line.target && line.target.manager && <span key="m" style={{ color: T.textFaint }}>{line.target.manager}</span>,
            line.per === "water" && !line.needsChoice && line.issuedBy && <span key="w" style={{ color: T.textFaint }}>{line.issuedBy}</span>,
            <span key="p" style={{ color: T.textSoft }}>{line.deferred ? `${money(line.price)} each` : money(line.price)}</span>,
            line.turnaround && <span key="t" style={{ color: T.textFaint }}>{line.turnaround}</span>,
          ].filter(Boolean).reduce((out, part, i) => (i ? [...out, <span key={`s${i}`} style={{ color: T.textFaint }}> · </span>, part] : [part]), [])}
        </span>
      </label>
      {line.needsChoice && (
        <select aria-label="Water authority" value={water} onChange={(e) => onWater(e.target.value)} style={{ ...inputStyle, marginTop: 4, marginLeft: 24, padding: "4px 8px", fontSize: 12 }}>
          <option value="">Choose the water authority…</option>
          {choices.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      )}
      {isTitle && (
        <input aria-label={`Volume and folio for ${label}`} value={vf} onChange={(e) => setVf(e.target.value)}
          onBlur={() => { if (vf !== volFolio) onVolFolio(vf); }}
          placeholder="Vol/Fol if known, e.g. 11691/178"
          style={{ ...inputStyle, marginTop: 4, marginLeft: 24, padding: "4px 8px", fontSize: 12, width: 230 }} />
      )}
      {line.note && <div style={{ marginLeft: 24 }}><Note>{line.note}</Note></div>}
    </div>
  );
};

// ── The orders, for whoever places them ─────────────────────────────────────

export const orderAddress = (order) =>
  (order.columns || []).map(c => c.property && c.property.address).filter(Boolean).join(" + ")
  || (order.property && order.property.address) || "";

const OrderList = ({ api, highlight, matterRef = "" }) => {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(highlight || null);

  const load = async () => {
    try {
      const r = await api(matterRef ? `/search-orders?matterRef=${encodeURIComponent(matterRef)}` : "/search-orders");
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || (r.status === 404 ? "Search orders are not on the service yet." : `Orders answered ${r.status}`));
      setOrders(data.orders || []);
    } catch (e) { setError(e.message); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const mark = async (orderId, key, status, reference) => {
    const r = await api(`/search-orders/${encodeURIComponent(orderId)}/lines`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, status, ...(reference !== undefined ? { reference } : {}) }),
    });
    const data = await r.json().catch(() => ({}));
    // A new sale whose client has not paid: said on the order, not over the list.
    if (r.status === 409 && data.code === "payment_pending") { setOrders(os => os.map(o => (o.orderId === orderId ? { ...o, paymentPending: true } : o))); return; }
    if (!r.ok || !data.ok) { setError(data.error || `Updating the line answered ${r.status}`); return; }
    setOrders(os => os.map(o => (o.orderId === orderId ? data.order : o)));
  };

  // An order saved before its matter existed, put on it now.
  const attach = async (orderId, ref) => {
    const r = await api(`/search-orders/${encodeURIComponent(orderId)}/matter`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ matterRef: ref }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data.ok) return data.error || `Attaching the order answered ${r.status}`;
    setOrders(os => os.map(o => (o.orderId === orderId ? data.order : o)));
    return "";
  };

  if (error) return <div role="alert" style={{ color: T.red, fontSize: 13 }}>{error}</div>;
  if (!orders) return <div style={{ fontSize: 13, color: T.textFaint }}>Loading orders…</div>;
  if (!orders.length) return <div style={{ fontSize: 13, color: T.textFaint }}>{matterRef ? "No searches ordered for this matter from here yet." : "No search orders yet."}</div>;

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10 }}>
      {orders.map(o => {
        const toDo = (o.lines || []).filter(l => l.status === "requested").length;
        return (
          <div key={o.orderId} style={{ borderBottom: `1px solid ${T.border}` }}>
            <button type="button" onClick={() => setOpen(open === o.orderId ? null : o.orderId)} aria-expanded={open === o.orderId}
              style={{ display: "flex", gap: 12, alignItems: "baseline", width: "100%", textAlign: "left", background: "none", border: "none", padding: "12px 14px", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
              <span style={{ flex: 1, fontSize: 13.5, color: T.text, fontWeight: 500 }}>{orderAddress(o)}</span>
              {o.matterRef && <span style={{ fontSize: 12, color: T.textFaint }}>{o.matterRef}</span>}
              <span data-order-state={o.paymentPending ? "awaiting-payment" : o.status} style={{ fontSize: 12, color: o.paymentPending ? T.textSoft : o.status === "requested" ? T.red : T.textSoft }}>
                {o.paymentPending ? "Awaiting client's payment" : o.status === "requested" ? `${toDo} to order` : o.status === "placed" ? "Placed" : "Cancelled"}
              </span>
              <span style={{ fontSize: 12, color: T.textSoft, width: 80, textAlign: "right" }}>${Number(o.total || 0).toFixed(2)}</span>
              <span style={{ fontSize: 12, color: T.textFaint, width: 90, textAlign: "right" }}>{o.createdAt ? new Date(o.createdAt).toLocaleDateString("en-AU") : ""}</span>
            </button>
            {open === o.orderId && (
              <div style={{ padding: "0 14px 14px" }}>
                <div style={{ fontSize: 12, color: T.textFaint, marginBottom: 8 }}>
                  Through {CHANNEL_LABEL[o.channel] || o.channel}{o.orderedBy ? ` · by ${o.orderedBy}` : ""}{o.notes ? ` · ${o.notes}` : ""}
                </div>
                {/* The middleware refuses to place or attach these until the
                    client pays (or the matter is reviewed without payment). */}
                {o.paymentPending && (
                  <div role="note" style={{ fontSize: 12.5, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
                    New sale {o.forMatter}: the client hasn't paid and no card is held. Nothing here can be placed until they pay, or the matter is reviewed without payment.
                  </div>
                )}
                {!o.matterRef && !o.paymentPending && <AttachToMatter onAttach={(ref) => attach(o.orderId, ref)} />}
                {(o.lines || []).map(l => (
                  <OrderLine key={l.key} line={l} columns={o.columns || []} onMark={(status, reference) => mark(o.orderId, l.key, status, reference)} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AttachToMatter = ({ onAttach }) => {
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const problem = await onAttach(ref.trim());
    setBusy(false);
    if (problem) setError(problem);
  };
  return (
    <form onSubmit={submit} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
      <span style={{ fontSize: 12.5, color: T.textSoft }}>Not on a matter yet.</span>
      <input aria-label="Matter to attach this order to" value={ref} onChange={(e) => setRef(e.target.value)}
        placeholder="Matter reference" style={{ ...inputStyle, padding: "4px 8px", fontSize: 12, width: 150 }} />
      <Button type="submit" disabled={busy || !ref.trim()} style={{ padding: "4px 12px", fontSize: 12 }}>Attach to matter</Button>
      {error && <span role="alert" style={{ fontSize: 12, color: T.red }}>{error}</span>}
    </form>
  );
};

const OrderLine = ({ line, columns, onMark }) => {
  const [ref, setRef] = useState(line.reference || "");
  useEffect(() => setRef(line.reference || ""), [line.reference]);
  const col = columns.find(c => c.column === line.column);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", borderTop: `1px solid ${T.border}`, flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 280px", fontSize: 13, color: T.text }}>
        {line.name}
        {line.target && line.target.label && <span style={{ color: T.textSoft }}> — {line.target.label}</span>}
        {line.issuedBy && <span style={{ color: T.textFaint }}> · {line.issuedBy}</span>}
        {columns.length > 1 && col && <span style={{ color: T.textFaint }}> · {col.label}</span>}
      </div>
      {line.received && <span style={{ fontSize: 11, color: T.textSoft, border: `1px solid ${T.borderMid}`, borderRadius: 999, padding: "1px 8px" }}>Received</span>}
      <span style={{ fontSize: 12, color: T.textSoft, width: 90 }}>{money(line.price)}</span>
      <input aria-label={`Supplier reference for ${line.name}`} value={ref} onChange={(e) => setRef(e.target.value)}
        onBlur={() => { if (ref !== (line.reference || "")) onMark(line.status, ref); }}
        placeholder="Supplier reference" style={{ ...inputStyle, padding: "4px 8px", fontSize: 12, width: 150 }} />
      <select aria-label={`Status of ${line.name}`} value={line.status} onChange={(e) => onMark(e.target.value, ref)} style={{ ...inputStyle, padding: "4px 8px", fontSize: 12 }}>
        {LINE_STATUS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
    </div>
  );
};

// ── The page ────────────────────────────────────────────────────────────────

export const SearchOrderPage = ({ api, onBack, matter = null }) => {
  const [tab, setTab] = useState("new");
  const [saved, setSaved] = useState(null);
  return (
    <div>
      {onBack && (
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 13, color: T.textFaint, fontFamily: "Inter, sans-serif", marginBottom: 20, padding: 0 }}>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="m15 18-6-6 6-6"/></svg>
          {matter ? `Back to ${matter.ref}` : "Back to dashboard"}
        </button>
      )}
      <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 24, color: T.text, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
        Order searches{matter ? <span style={{ color: T.textFaint }}> · {matter.ref}</span> : null}
      </h1>
      <p style={{ fontSize: 13, color: T.textSoft, margin: "0 0 16px", maxWidth: 760, lineHeight: 1.5 }}>
        {matter
          ? "The certificates this matter's property needs, for each title, with what each costs. Saving it adds each one to the matter's searches, waiting for its document, and makes an order for somebody to place with the supplier."
          : "Type the address and the certificates a section 32 needs are listed for each title, with what each costs. Saving it makes an order for somebody to place with the supplier — nothing is ordered automatically yet."}
      </p>
      <div role="tablist" style={{ display: "flex", gap: 20, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        {[["new", "New order"], ["orders", "Orders"]].map(([k, label]) => (
          <button key={k} role="tab" type="button" aria-selected={tab === k} onClick={() => setTab(k)}
            style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === k ? T.red : "transparent"}`, padding: "0 0 8px", marginBottom: -1, fontSize: 13.5, fontWeight: 500, fontFamily: "Inter, sans-serif", color: tab === k ? T.text : T.textSoft, cursor: "pointer" }}>
            {label}
          </button>
        ))}
      </div>
      {saved && tab === "orders" && (
        <div role="status" style={{ fontSize: 13, color: T.text, marginBottom: 12 }}>
          Order saved — ${Number(saved.total || 0).toFixed(2)} for {(saved.lines || []).length} certificates{saved.matterRef ? `, added to ${saved.matterRef}'s searches` : ""}. Mark each line as you place it.
        </div>
      )}
      {tab === "new"
        ? <NewOrder api={api} forMatter={matter} onSaved={(order) => { setSaved(order); setTab("orders"); }} />
        : <OrderList api={api} highlight={saved && saved.orderId} matterRef={matter ? matter.ref : ""} />}
    </div>
  );
};

// ── On the matter page ──────────────────────────────────────────────────────

// What has been ordered for this matter from here, and the way in to order
// more. Quiet when the service has no orders route, so an older middleware
// shows nothing rather than an error on every matter.
export const orderProgress = (order) => {
  const live = (order.lines || []).filter(l => l.status !== "cancelled" && l.status !== "not_required");
  return {
    count: live.length,
    received: live.filter(l => l.received).length,
    toPlace: live.filter(l => l.status === "requested").length,
  };
};

// `soon` greys the button while ordering is unfinished (the main module's
// `UNFINISHED`), so the classic page says what the practice ribbon says; the
// orders already placed are still listed.
export const MatterOrders = ({ api, matterRef, onOrder, soon = false }) => {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await api(`/search-orders?matterRef=${encodeURIComponent(matterRef)}`);
        const data = await r.json().catch(() => ({}));
        if (live) setOrders(r.ok && data.ok ? data.orders || [] : []);
      } catch { if (live) setOrders([]); }
    })();
    return () => { live = false; };
  }, [api, matterRef]);

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
      <div style={{ flex: "1 1 240px", fontSize: 12, color: T.textSoft, lineHeight: 1.6 }}>
        {(orders || []).map((o) => {
          const p = orderProgress(o);
          return (
            <div key={o.orderId}>
              Ordered {o.createdAt ? new Date(o.createdAt).toLocaleDateString("en-AU") : ""} through {CHANNEL_LABEL[o.channel] || o.channel}
              {" · "}{p.received} of {p.count} received{p.toPlace ? ` · ${p.toPlace} still to place` : ""}
            </div>
          );
        })}
      </div>
      <Button onClick={soon ? undefined : onOrder} disabled={soon} title={soon ? "Coming soon — not working yet" : undefined}
        style={{ padding: "5px 14px", fontSize: 12 }}>Order searches</Button>
    </div>
  );
};

export default SearchOrderPage;
